'use client';
import { FC, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ContextProviderProps } from '../helpers/CombinedContextProvider';
import { httpGet } from '../../components/utils';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { useGetIsCloudOnlyRequest } from '../api/useGetIsCloudOnlyRequest';
import { useGetBlockedPagesRequest } from '../api/useGetBlockedPagesRequest';

export type SYSTEM_MODE = (typeof SYSTEM_MODES)[keyof typeof SYSTEM_MODES];
export type LicenseValue = number | null | 'INFINITY' | 'N/A';
export type LicenseModuleState = 'enabled' | 'disabled' | 'poc';

export enum PricingModules {
    BASE_PLATFORM = 'basePlatform',
    MFA_USERS = 'mfaUsers',
    AUTHENTICATION_FIREWALL = 'authenticationFirewall',
    NHI_VISIBILITY = 'nhiVisibility',
    NHI_PROTECTION = 'nhiProtection',
    ISPM = 'ispm',
    ITDR = 'itdr',
    PRIVILEGED_ACCESS_SECURITY = 'privilegedAccessSecurity',
    ACCESS_ANALYSIS = 'accessAnalysis',
    AI_AGENTS_SECURITY = 'aiAgentsSecurity',
    AI_AGENTS_PROTECTION = 'aiAgentsProtection'
}

export interface PricingModuleStatus {
    anyEnabled: boolean;
    onPremEnabled: boolean;
    cloudEnabled: boolean;
    onPremStatus: LicenseModuleState;
    cloudStatus: LicenseModuleState;
    value: LicenseValue;
}

export type PricingModulesStatus = Record<PricingModules, PricingModuleStatus>;

export const LICENSE_MODULE_DEFAULT_ONPREM_ONLY: PricingModuleStatus = {
    anyEnabled: true,
    onPremEnabled: true,
    cloudEnabled: false,
    onPremStatus: 'enabled',
    cloudStatus: 'disabled',
    value: 'N/A'
};

export const LICENSE_MODULE_DEFAULT_CLOUD_ONLY: PricingModuleStatus = {
    anyEnabled: true,
    onPremEnabled: false,
    cloudEnabled: true,
    onPremStatus: 'disabled',
    cloudStatus: 'enabled',
    value: 'N/A'
};

export const LICENSE_MODULE_DEFAULT_OFF: PricingModuleStatus = {
    anyEnabled: false,
    onPremEnabled: false,
    cloudEnabled: false,
    onPremStatus: 'disabled',
    cloudStatus: 'disabled',
    value: null
};

export const LICENSE_MODULES_DEFAULTS: PricingModulesStatus = {
    [PricingModules.BASE_PLATFORM]: LICENSE_MODULE_DEFAULT_ONPREM_ONLY,
    [PricingModules.MFA_USERS]: LICENSE_MODULE_DEFAULT_OFF,
    [PricingModules.AUTHENTICATION_FIREWALL]: LICENSE_MODULE_DEFAULT_OFF,
    [PricingModules.NHI_VISIBILITY]: LICENSE_MODULE_DEFAULT_OFF,
    [PricingModules.NHI_PROTECTION]: LICENSE_MODULE_DEFAULT_OFF,
    [PricingModules.ISPM]: LICENSE_MODULE_DEFAULT_OFF,
    [PricingModules.ITDR]: LICENSE_MODULE_DEFAULT_OFF,
    [PricingModules.PRIVILEGED_ACCESS_SECURITY]: LICENSE_MODULE_DEFAULT_OFF,
    [PricingModules.ACCESS_ANALYSIS]: LICENSE_MODULE_DEFAULT_OFF,
    [PricingModules.AI_AGENTS_SECURITY]: LICENSE_MODULE_DEFAULT_OFF,
    [PricingModules.AI_AGENTS_PROTECTION]: LICENSE_MODULE_DEFAULT_OFF
};

interface PricingModuleContextInterface {
    pricingModulesState: PricingModulesStatus | null;
    fetchPricingModulesState: () => Promise<void>;
    isAnyModuleCloudEnabled: boolean;
    isCloudOnly: boolean;
    isPageBlocked: (pageId: number) => boolean;
}

const DEFAULT_VALUE: PricingModuleContextInterface = {
    pricingModulesState: LICENSE_MODULES_DEFAULTS,
    fetchPricingModulesState: () => Promise.resolve(),
    isAnyModuleCloudEnabled: false,
    isCloudOnly: false,
    isPageBlocked: (pageId: number) => false
};

const PricingModuleContext = createContext<PricingModuleContextInterface>(DEFAULT_VALUE);

export const PricingModuleContextProvider: FC<ContextProviderProps> = ({ children }) => {
    const [pricingModulesState, setPricingModulesState] = useState<PricingModulesStatus | null>(null);
    const { isCloudOnly } = useGetIsCloudOnlyRequest();
    const { blockedPages } = useGetBlockedPagesRequest();

    const fetchPricingModulesState = async () => {
        try {
            const response = await httpGet(`/license/getPricingModuleStatuses`);
            setPricingModulesState(response.data);
        } catch (error) {
            console.error('Failed to get feature flags', error);
        }
    };
    useEffect(() => {
        fetchPricingModulesState();
    }, []);

    const value = useMemo((): PricingModuleContextInterface => {
        const isAnyModuleCloudEnabled = pricingModulesState
            ? Object.values(pricingModulesState).some((module: PricingModuleStatus) => module.cloudEnabled)
            : false;

        return {
            pricingModulesState,
            fetchPricingModulesState,
            isAnyModuleCloudEnabled,
            isCloudOnly: isCloudOnly ?? DEFAULT_VALUE.isCloudOnly,
            isPageBlocked: (pageId: number) => blockedPages?.[pageId] ?? false
        };
    }, [isCloudOnly, pricingModulesState, blockedPages]);

    return <PricingModuleContext.Provider value={value}>{children}</PricingModuleContext.Provider>;
};

export const usePricingModulesState = (): PricingModuleContextInterface => useContext(PricingModuleContext);

export const PricingModuleContextConsumer = PricingModuleContext.Consumer;
